import { renataChatSearch } from "@/lib/renata/experience/chat-search";
/**
 * Renata Acciones Dashboard — FASE 6 Acción Orchestrator
 * /app/renata/tasks
 */
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import {
  Zap, RefreshCw, CheckCircle2, Clock, XCircle, AlertTriangle,
  ChevronRight, Filter, BarChart2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { toast } from "sonner";
import type { TaskInstance, TaskStatus, TaskDefinition, TaskProposalPreview } from "@/lib/renata/tasks/types";
import { TaskPreview } from "@/components/renata/TaskPreview";

export const Route = createFileRoute("/app/renata/tasks")({
  component: RenataTasksPage,
  head: () => ({ meta: [{ title: "Acciones — Renata — Structura Market" }] }),
});

const STATUS_CONFIG: Record<TaskStatus, { label: string; icon: React.ComponentType<{ className?: string }>; className: string }> = {
  detecting:  { label: "Detectando",  icon: Clock,         className: "text-slate-500 bg-slate-50 border-slate-200 dark:bg-slate-900/30 dark:border-slate-700" },
  collecting: { label: "Recopilando", icon: Clock,         className: "text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800" },
  ready:      { label: "Listo",       icon: CheckCircle2,  className: "text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800" },
  proposed:   { label: "Propuesto",   icon: Zap,           className: "text-violet-600 bg-violet-50 border-violet-200 dark:bg-violet-950/30 dark:border-violet-800" },
  approved:   { label: "Aprobado",    icon: CheckCircle2,  className: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  executed:   { label: "Ejecutado",   icon: CheckCircle2,  className: "text-emerald-700 bg-emerald-100 border-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-700" },
  rejected:   { label: "Rechazado",   icon: XCircle,       className: "text-red-600 bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800" },
  cancelled:  { label: "Cancelado",   icon: XCircle,       className: "text-slate-500 bg-slate-50 border-slate-200 dark:bg-slate-900/30 dark:border-slate-700" },
  abandoned:  { label: "Abandonado",  icon: AlertTriangle, className: "text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800" },
};

const FILTER_STATUSES: Array<{ value: string; label: string }> = [
  { value: "all",        label: "Todas" },
  { value: "collecting", label: "En progreso" },
  { value: "proposed",   label: "Esperando aprobación" },
  { value: "approved",   label: "Aprobadas" },
  { value: "executed",   label: "Ejecutadas" },
  { value: "rejected",   label: "Rechazadas" },
  { value: "cancelled",  label: "Canceladas" },
];

function TaskCard({
  instance,
  definitions,
  onCancel,
}: {
  instance: TaskInstance;
  definitions: TaskDefinition[];
  onCancel: (id: string) => void;
}) {
  const def = definitions.find((d) => d.id === instance.task_id);
  const statusCfg = STATUS_CONFIG[instance.status];
  const Icon = statusCfg.icon;
  const [showPreview, setShowPreview] = useState(false);

  const preview = instance.proposal?._preview as TaskProposalPreview | undefined;

  return (
    <div className={cn("rounded-xl border p-4 space-y-3", statusCfg.className)}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-sm font-semibold">{def?.title ?? instance.task_id}</p>
            <Badge variant="outline" className="text-[10px]">{statusCfg.label}</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {new Date(instance.created_at).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" })}
          </p>
        </div>
      </div>

      {preview && showPreview && (
        <TaskPreview preview={preview} />
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {preview && (
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setShowPreview(!showPreview)}>
            <ChevronRight className={cn("h-3 w-3 transition-transform", showPreview && "rotate-90")} />
            {showPreview ? "Ocultar" : "Ver detalle"}
          </Button>
        )}
        {(instance.status === "collecting" || instance.status === "proposed") && (
          <>
            <Link to="/app/renata/chat" search={renataChatSearch()}>
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1">
                <Zap className="h-3 w-3" /> Continuar en chat
              </Button>
            </Link>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => onCancel(instance.id)}
            >
              Cancelar
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function StatsBar({ stats }: { stats: Record<string, unknown> }) {
  const byStatus = (stats.byStatus as Array<{ status: string; count: number }>) ?? [];
  const executed = byStatus.find((s) => s.status === "executed")?.count ?? 0;
  const proposed = byStatus.find((s) => s.status === "proposed")?.count ?? 0;
  const total = byStatus.reduce((a, s) => a + s.count, 0);
  const avgSec = Number(stats.avgSeconds ?? 0);
  const totalCost = Number(stats.totalCostMicrocents ?? 0);
  const topModel = stats.topModel as string | null | undefined;

  const kpis = [
    { label: "Total tareas", value: total },
    { label: "Ejecutadas",   value: executed },
    { label: "Esperando aprobación", value: proposed },
    { label: "Tiempo prom.", value: avgSec > 0 ? `${Math.round(avgSec)}s` : "—" },
    ...(totalCost > 0
      ? [{ label: "Costo AI", value: `$${(totalCost / 100_000_000).toFixed(4)}` }]
      : []),
    ...(topModel ? [{ label: "Modelo ppal.", value: topModel }] : []),
  ];

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {kpis.map((kpi) => (
        <div key={kpi.label} className="rounded-xl border bg-card p-3 text-center">
          <p className="text-xl font-bold tabular-nums truncate">{kpi.value}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{kpi.label}</p>
        </div>
      ))}
    </div>
  );
}

function RenataTasksPage() {
  const tenantId = useWorkspaceTenantId();
  const [instances, setInstances]     = useState<TaskInstance[]>([]);
  const [definitions, setDefinitions] = useState<TaskDefinition[]>([]);
  const [stats, setStats]             = useState<Record<string, unknown>>({});
  const [status, setStatus]           = useState<string>("all");
  const [loading, setLoading]         = useState(true);
  const [total, setTotal]             = useState(0);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [listResp, statsResp] = await Promise.all([
        fetch(`/api/renata/tasks?status=${status}&limit=50`, {
          credentials: "include",
          headers: clientRequestHeaders(tenantId),
        }),
        fetch("/api/renata/tasks?stats=1", {
          credentials: "include",
          headers: clientRequestHeaders(tenantId),
        }),
      ]);
      const listData = await listResp.json() as {
        definitions: TaskDefinition[];
        instances: TaskInstance[];
        total: number;
      };
      const statsData = await statsResp.json() as Record<string, unknown>;
      setDefinitions(listData.definitions ?? []);
      setInstances(listData.instances ?? []);
      setTotal(listData.total ?? 0);
      setStats(statsData);
    } catch {
      toast.error("Error al cargar las tareas");
    } finally {
      setLoading(false);
    }
  }, [tenantId, status]);

  useEffect(() => { void load(); }, [load]);

  async function handleCancel(id: string) {
    if (!tenantId) return;
    try {
      await fetch("/api/renata/tasks", {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json", ...clientRequestHeaders(tenantId) },
        body: JSON.stringify({ id, action: "cancel" }),
      });
      toast.success("Tarea cancelada");
      void load();
    } catch {
      toast.error("Error al cancelar la tarea");
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-base font-semibold flex items-center gap-2">
            <Zap className="h-4 w-4 text-violet-500" />
            Acciones operativas
          </h1>
          <p className="text-sm text-muted-foreground">
            Historial de tareas creadas con Renata · {total} total
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-2">
          <RefreshCw className="h-3.5 w-3.5" />
          Actualizar
        </Button>
      </div>

      <StatsBar stats={stats} />

      {/* Filters */}
      <div className="flex gap-1 flex-wrap">
        <Filter className="h-4 w-4 mt-1.5 text-muted-foreground" />
        {FILTER_STATUSES.map((f) => (
          <Button
            key={f.value}
            variant={status === f.value ? "default" : "outline"}
            size="sm"
            className={cn("h-7 text-xs", status === f.value && "bg-violet-600 hover:bg-violet-700")}
            onClick={() => setStatus(f.value)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {loading && (
        <div className="flex h-32 items-center justify-center">
          <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && instances.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <BarChart2 className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No hay tareas para este filtro. ¡Empezá una en el chat!
          </p>
          <Link to="/app/renata/chat" search={renataChatSearch()}>
            <Button size="sm" className="bg-violet-600 hover:bg-violet-700 gap-2">
              <Zap className="h-3.5 w-3.5" />
              Ir al chat
            </Button>
          </Link>
        </div>
      )}

      <div className="space-y-3">
        {instances.map((inst) => (
          <TaskCard
            key={inst.id}
            instance={inst}
            definitions={definitions}
            onCancel={handleCancel}
          />
        ))}
      </div>
    </div>
  );
}
